import { FormLayout, Modal, TextField, Text, Form, InlineGrid, InlineStack, Select, Layout } from '@shopify/polaris';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import helpers from 'helpers';
import { lengthLessThan, lengthMoreThan, useField, useForm } from '@shopify/react-form';
import { TypedUser } from 'interface/user.model';
import DateTimeInput from 'components/dateTimeInput';
import __ from 'languages/index';
import { useMutateCreateCustomer } from 'queries/customer.query';
import Capacity from 'components/Capacity';

/**
 *   Create upload Modal for Notification
 */

// eslint-disable-next-line import/no-anonymous-default-export

function CustomerNew({
  onClose,
  show,
  defaultNumber = '',
  defaultEmail = '',
  defaultName = '',
}: {
  onClose: (userData?: TypedUser) => void;
  show: boolean;
  defaultNumber?: string;
  defaultEmail?: string;
  defaultName?: string;
}) {
  const { mutateAsync: createEntity } = useMutateCreateCustomer();

  const history = useNavigate();

  const onCloseCallback = useCallback((args: any) => {
    onClose(args);
  }, []);

  /**
   * Save and close or save and moving to detail?
   */
  const saveAndClose = useRef(false);

  const useFields = {
    display_name: useField<string>({
      value: defaultName,
      validates: [lengthLessThan(50, 'Không được dài hơn 50 ký tự.'), lengthMoreThan(1, 'Tên phải dài hơn 1 ký tự.')],
    }),

    bio: useField<string>({
      value: '',
      validates: [lengthLessThan(250, 'No more than 250 characters.')],
    }),

    user_phonenumber: useField<string>({
      value: defaultNumber,
      validates: [
        (inputValue) => {
          if (inputValue && !helpers.isPhoneNumber(inputValue)) {
            return 'Số điện thoại không hợp lệ';
          }
        },
      ],
    }),

    user_birthday: useField<any>({
      value: '',
      validates: [],
    }),

    user_address: useField<string>({
      value: '',
      validates: [lengthLessThan(150, 'No more than 150 characters.')],
    }),

    user_address_city: useField<string>({
      value: '',
      validates: [],
    }),

    user_address_ward: useField<string>({
      value: '',
      validates: [],
    }),

    user_title: useField<string>({
      value: '',
      validates: [],
    }),

    user_email: useField<string>({
      value: defaultEmail,
      validates: [
        (inputValue) => {
          if (inputValue)
            if (!helpers.isEmail(inputValue)) {
              return 'Your email is not valid!';
            }
        },
      ],
    }),
  };

  const { fields, submit, submitting, dirty, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        // create new

        let userData = await createEntity({
          display_name: values.display_name,
          bio: values.bio,
          user_phonenumber: values.user_phonenumber,
          user_email: values.user_email,
          user_address: values.user_address,
          user_address_city: values.user_address_city,
          user_address_ward: values.user_address_ward,
          user_birthday: values.user_birthday || null,
          user_title: values.user_title,
        });

        if (saveAndClose.current === true) {
          await helpers.sleep(1000);
          onCloseCallback(userData);
        } else {
          await helpers.sleep(1000);
          history('/customer/view/' + userData?.user_id);
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  useEffect(() => {
    if (!show) {
      reset();
      helpers.resetForm(useFields);
    }
  }, [show]);

  const submitMode = useCallback((_mode: string) => {
    if (_mode === 'saveandclose') {
      saveAndClose.current = true;
    } else {
      saveAndClose.current = false;
    }
    submit();
  }, []);

  return (
    <Modal
      open={show}
      onClose={() => onCloseCallback(null)}
      title={'Thêm Khách hàng mới'}
      primaryAction={{
        content: 'Tạo & đóng',
        disabled: !dirty,
        loading: submitting,
        onAction: () => {
          submitMode('saveandclose');
        },
      }}
      secondaryActions={[
        {
          content: 'Đóng',
          onAction: () => onCloseCallback(null),
        },
        {
          content: 'Tạo & Xem',
          disabled: !dirty || submitting,
          onAction: () => {
            submitMode('any');
          },
        },
      ]}
    >
      <Modal.Section>
        <Capacity current_user_can={['manage_customers', 'edit_customers']} onFail={<>Bạn không có quyền truy cập.</>}>
          <Form onSubmit={submit}>
            <FormLayout>
              <TextField
                label="Tên hiển thị"
                autoComplete="off"
                error={fields.display_name.error}
                value={fields.display_name.value}
                onChange={(e) => fields.display_name.onChange(helpers.capitalizeWords(e))}
                connectedLeft={
                  <Select
                    value={useFields.user_title.value}
                    options={[
                      { value: '', label: 'Không set' },
                      { value: 'mr', label: 'Anh' },
                      { value: 'miss', label: 'Cô' },
                      { value: 'mrs', label: 'Chị' },

                      { value: 'madam', label: 'Quý bà' },
                      { value: 'sir', label: 'Quý ông' },
                    ]}
                    onChange={(e) => useFields.user_title.onChange(e)}
                    label={''}
                  />
                }
              />

              <InlineGrid gap="400" columns={2}>
                <TextField label="Số điện thoại" autoComplete="off" {...fields.user_phonenumber} type="tel" inputMode="numeric" pattern="[0-9]*" />

                <TextField label="Email" autoComplete="off" {...fields.user_email} />
              </InlineGrid>

              <DateTimeInput
                onDateChange={(dateObjectOrnull) => {
                  if (!dateObjectOrnull) {
                    fields.user_birthday.onChange(``);
                  } else {
                    let { day, month, year } = dateObjectOrnull;
                    fields.user_birthday.onChange(`${year}-${month}-${day}`);
                  }
                }}
                label={'Sinh nhật'}
                initialDate={fields.user_birthday.value}
                error={fields.user_birthday.error}
              />

              <TextField label="Số nhà, đường, thôn xóm" autoComplete="off" {...fields.user_address} />

              <TextField label="Ghi chú" autoComplete="off" multiline={2} {...fields.bio} />
            </FormLayout>
          </Form>
          <br />
          <InlineStack gap={'500'}>
            <Text as="p" variant="bodyMd">
              Bạn sẽ thêm được các thông tin khác trong giao diện chỉnh sửa toàn diện
            </Text>
          </InlineStack>
        </Capacity>
      </Modal.Section>
    </Modal>
  );
}

export default memo(CustomerNew);

